/**
 * PDF Worker Setup for react-pdf v10 with Vite
 *
 * This module configures the PDF.js worker required for react-pdf to function.
 * Must be imported before any react-pdf components are used.
 *
 * NOTE: We use the CDN-hosted worker to avoid MIME type issues with .mjs files
 * being served as application/octet-stream by some nginx configurations.
 */

import { pdfjs } from 'react-pdf';

// Get the pdfjs version for CDN URL
const PDFJS_VERSION = pdfjs.version;

// Initialize worker with proper error handling
let workerInitialized = false;
let workerInitError: Error | null = null;
// Always use CDN-hosted worker to avoid MIME type issues with .mjs files
const workerSrc = `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.mjs`;

if (typeof window !== 'undefined') {
  try {
    // Force set the worker source immediately to prevent react-pdf from auto-detecting
    // a local worker file during build/bundling
    pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
    
    // Verify the worker source is set correctly
    if (pdfjs.GlobalWorkerOptions.workerSrc !== workerSrc) {
      // If it was changed, force it back to CDN
      pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
      console.warn('PDF worker source was changed, forcing CDN URL');
    }
    
    // Verify worker can be accessed (basic check)
    // Note: The actual worker loading happens asynchronously when first used
    workerInitialized = true;
  } catch (error) {
    console.error('Failed to initialize PDF worker:', error);
    workerInitError = error instanceof Error ? error : new Error(String(error));
  }
}

/**
 * Check if the worker is properly initialized
 */
export function isWorkerInitialized(): boolean {
  return workerInitialized && !!pdfjs.GlobalWorkerOptions.workerSrc;
}

/**
 * Get any initialization error
 */
export function getWorkerInitError(): Error | null {
  return workerInitError;
}

/**
 * Wait for the worker to be ready before using PDF components
 * This helps prevent race conditions where components try to use the worker before it's initialized
 */
export async function ensureWorkerReady(): Promise<void> {
  if (!workerInitialized && workerInitError) {
    throw new Error(`PDF worker failed to initialize: ${workerInitError.message}`);
  }
  
  // Always ensure worker source is set to CDN (prevent auto-detection of local worker)
  if (pdfjs.GlobalWorkerOptions.workerSrc !== workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
    if (pdfjs.GlobalWorkerOptions.workerSrc !== workerSrc) {
      console.warn('PDF worker source was changed, forcing CDN URL');
    }
  }
  
  // Give the worker a moment to initialize if needed
  if (!workerInitialized) {
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  // Final verification - ensure CDN URL is still set
  if (pdfjs.GlobalWorkerOptions.workerSrc !== workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
    console.warn('PDF worker source was reset, re-initializing with CDN URL...');
  }
}

/**
 * Re-initialize the worker if it was terminated
 * This can be called when encountering worker errors
 */
export function reinitializeWorker(): void {
  if (typeof window !== 'undefined') {
    try {
      // Force set to CDN URL
      pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
      
      // Verify it was set correctly
      if (pdfjs.GlobalWorkerOptions.workerSrc !== workerSrc) {
        pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
        console.warn('PDF worker source was changed during re-initialization, forcing CDN URL');
      }
      
      workerInitialized = true;
      workerInitError = null;
      console.log('PDF worker re-initialized with CDN URL');
    } catch (error) {
      console.error('Failed to re-initialize PDF worker:', error);
      workerInitError = error instanceof Error ? error : new Error(String(error));
    }
  }
}

/**
 * Export the configured pdfjs instance for use in components
 */
export { pdfjs };